import {
  type Dispatch,
  type ElementRef,
  type ComponentPropsWithoutRef,
  type PropsWithChildren,
  type RefObject,
  type SetStateAction,
  createContext,
  forwardRef,
  useRef,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { composeRefs } from '@rtkwlf/fenrir-react';
import { faSidebarFlip } from '@rtkwlf/pro-solid-svg-icons/faSidebarFlip';
import clsx from 'clsx';
import { useHotkeys } from 'react-hotkeys-hook';
import {
  PanelGroup,
  Panel,
  PanelResizeHandle,
  type ImperativePanelHandle,
  getPanelElement,
} from 'react-resizable-panels';
import { Link, useLocation } from 'react-router-dom';

import { Tabs, Tab } from '@packages/fenrir/components/tabs';
import { ToggleButton } from '@packages/fenrir/components/toggle-button';
import { Tooltip } from '@packages/fenrir/components/tooltip';
import { Typography } from '@packages/fenrir/components/typography';
import { useMediaQuery } from '@packages/hooks/use-media-query';

import { useSecurityJourneyTranslations } from '../../locales/use-security-journey-translations';
import { Kbd, KbdShortcut } from '../kbd-shortcut/kbd-shortcut';

import './page.css';

/* -------------------------------------------------------------------------------------------------
 * Constants
 * -----------------------------------------------------------------------------------------------*/
const PANEL_TRANSITION_CLASS = 'panel-transition';
const PREVIEW_PANEL_ID = 'preview';

/* -------------------------------------------------------------------------------------------------
 * PageContext
 * -----------------------------------------------------------------------------------------------*/
type PageContextProps = {
  collapsible: boolean;
  setCollapsible: Dispatch<SetStateAction<boolean>>;
  collapsed: boolean;
  collapse: () => void;
  expand: (animate?: boolean) => void;
  previewPanelRef: RefObject<ImperativePanelHandle | null>;
  togglePreviewPanel: (animate?: boolean) => void;
};

const PageContext = createContext<PageContextProps | null>(null);

const usePageContext = () => {
  const context = useContext(PageContext);

  if (context == null) {
    throw new Error(
      'Cannot access PageContext. Ensure your component is wrapped in `Page`.',
    );
  }

  return context;
};
/* -------------------------------------------------------------------------------------------------
 * Page
 * -----------------------------------------------------------------------------------------------*/
type PageElement = ElementRef<'div'>;
type PageProps = ComponentPropsWithoutRef<'div'>;

const Page = forwardRef<PageElement, PropsWithChildren<PageProps>>(
  ({ children, className, ...rest }, ref) => {
    /**
     * init state to track if preview panel is collapsed
     */
    const [collapsed, setCollapsed] = useState(true);

    /**
     * init state to track if preview panel can be collapsed
     */
    const [collapsible, setCollapsible] = useState(false);

    /**
     * init preview panel ref
     */
    const previewPanelRef = useRef<ImperativePanelHandle>(null);

    /**
     * check if user prefers reduced motion
     */
    const isReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    /**
     * handles toggling the preview panel
     */
    const togglePreviewPanel = (animate?: boolean) => {
      if (previewPanelRef.current == null) {
        return;
      }

      if (previewPanelRef.current.isCollapsed()) {
        expand(animate);
      } else {
        collapse();
      }
    };

    /**
     * adds transition class to preview panel. allows the preview panel
     * to transition open and closed
     */
    const addTransitionClass = () => {
      const previewPanelElement = getPanelElement(PREVIEW_PANEL_ID);

      if (previewPanelElement) {
        previewPanelElement.classList.add(PANEL_TRANSITION_CLASS);
      }
    };

    /**
     * collapses the preview panel
     */
    const collapse = useCallback(() => {
      if (previewPanelRef.current == null) {
        return;
      }

      previewPanelRef.current.collapse();
      setCollapsed(true);
    }, []);

    /**
     * expand the preview panel
     */
    const expand = useCallback(
      (animate?: boolean) => {
        if (previewPanelRef.current == null) {
          return;
        }

        if (animate && !isReducedMotion) {
          addTransitionClass();
        }

        previewPanelRef.current.expand();
        setCollapsed(false);
      },
      [isReducedMotion],
    );

    /**
     * init keyboard shortcut for toggling the preview panel
     */
    useHotkeys('meta+i', () => togglePreviewPanel(true), {
      enabled: !(previewPanelRef.current == null) && collapsible,
    });

    return (
      <PageContext.Provider
        value={{
          collapsible,
          setCollapsible,
          collapse,
          collapsed,
          expand,
          previewPanelRef,
          togglePreviewPanel,
        }}
      >
        <div {...rest} ref={ref} className={clsx('layout-v2 page', className)}>
          {children}
        </div>
      </PageContext.Provider>
    );
  },
);

Page.displayName = 'Page';

/* -------------------------------------------------------------------------------------------------
 * PageHeader
 * -----------------------------------------------------------------------------------------------*/
type PageHeaderElement = ElementRef<'header'>;
type PageHeaderProps = ComponentPropsWithoutRef<'header'>;

const PageHeader = forwardRef<
  PageHeaderElement,
  PropsWithChildren<PageHeaderProps>
>(({ children, className, ...rest }, ref) => {
  /**
   * subscribe to page context
   */
  const { collapsible, collapsed, togglePreviewPanel, previewPanelRef } =
    usePageContext();

  /**
   * get an instance of the translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * compute the title for the toggle button tooltip
   */
  const title = collapsed
    ? t('common.previewPanel.tooltipExpand')
    : t('common.previewPanel.tooltipCollapse');

  return (
    <header {...rest} ref={ref} className={clsx('page-header', className)}>
      {children}
      <div className="page-header-actions">
        {Boolean(!(previewPanelRef.current == null) && collapsible) && (
          <Tooltip
            enterDelay={600}
            title={
              <KbdShortcut title={title}>
                <Kbd meta />
                <Kbd letter="i" />
              </KbdShortcut>
            }
          >
            <ToggleButton
              value=""
              onClick={() => togglePreviewPanel(true)}
              size="small"
              selected={!collapsed}
            >
              <FontAwesomeIcon icon={faSidebarFlip} />
            </ToggleButton>
          </Tooltip>
        )}
      </div>
    </header>
  );
});

PageHeader.displayName = 'PageHeader';

/* -------------------------------------------------------------------------------------------------
 * PageTitle
 * -----------------------------------------------------------------------------------------------*/
type PageTitleElement = ElementRef<typeof Typography>;
type PageTitleComponentProps = ComponentPropsWithoutRef<typeof Typography>;
type PageTitleProps = Omit<PageTitleComponentProps, 'variant' | 'paragraph'>;

const PageTitle = forwardRef<
  PageTitleElement,
  PropsWithChildren<PageTitleProps>
>(({ children, className, ...rest }, ref) => {
  return (
    <Typography
      {...rest}
      variant="h1"
      ref={ref}
      className={clsx('page-title', className)}
    >
      {children}
    </Typography>
  );
});

PageTitle.displayName = 'PageTitle';

/* -------------------------------------------------------------------------------------------------
 * PageContent
 * -----------------------------------------------------------------------------------------------*/
type PageContentElement = ElementRef<'main'>;
type PageContentComponentProps = ComponentPropsWithoutRef<'main'>;
type PageContentProps = PageContentComponentProps;

const PageContent = forwardRef<
  PageContentElement,
  PropsWithChildren<PageContentProps>
>(({ children, className, ...rest }, ref) => {
  return (
    <main {...rest} className={clsx('page-content', className)} ref={ref}>
      {children}
    </main>
  );
});

PageContent.displayName = 'PageContent';

/* -------------------------------------------------------------------------------------------------
 * PagePanel
 * -----------------------------------------------------------------------------------------------*/
type PagePanelElement = ElementRef<'section'>;
type PagePanelComponentProps = ComponentPropsWithoutRef<'section'>;
type PagePanelProps = PagePanelComponentProps;

const PagePanel = forwardRef<
  PagePanelElement,
  PropsWithChildren<PagePanelProps>
>(({ children, className, ...rest }, ref) => {
  return (
    <section {...rest} ref={ref} className={clsx('page-panel', className)}>
      {children}
    </section>
  );
});

PagePanel.displayName = 'PagePanel';

/* -------------------------------------------------------------------------------------------------
 * PageResizableContent
 * -----------------------------------------------------------------------------------------------*/
type PageResizableContentElement = ElementRef<typeof PanelGroup>;
type PageResizableContentComponentProps = ComponentPropsWithoutRef<
  typeof PanelGroup
>;
type PageResizableContentProps = Omit<
  PageResizableContentComponentProps,
  'direction' | 'id' | 'keyboardResizeBy' | 'onLayout' | 'storage'
>;

const PageResizableContent = forwardRef<
  PageResizableContentElement,
  PropsWithChildren<PageResizableContentProps>
>(({ children, className, ...rest }, ref) => {
  return (
    <PanelGroup
      {...rest}
      className={clsx('page-content page-resizable-content', className)}
      direction="horizontal"
      tagName="main"
      ref={ref}
    >
      {children}
    </PanelGroup>
  );
});

PageResizableContent.displayName = 'PageResizableContent';

/* -------------------------------------------------------------------------------------------------
 * PageResizablePanel
 * -----------------------------------------------------------------------------------------------*/
type PageResizablePanelElement = ElementRef<typeof Panel>;
type PageResizablePanelComponentProps = ComponentPropsWithoutRef<typeof Panel>;
type PageResizablePanelProps = Omit<
  PageResizablePanelComponentProps,
  'collapsible' | 'collapsedSize' | 'defaultSize' | 'minSize' | 'maxSize'
>;

const PageResizablePanel = forwardRef<
  PageResizablePanelElement,
  PropsWithChildren<PageResizablePanelProps>
>(({ children, className, ...rest }, ref) => {
  return (
    <Panel
      {...rest}
      ref={ref}
      className={clsx('page-panel page-resizable-panel', className)}
    >
      <div className="page-panel-overflow">{children}</div>
    </Panel>
  );
});

PageResizablePanel.displayName = 'PageResizablePanel';

/* -------------------------------------------------------------------------------------------------
 * PagePanelHeader
 * -----------------------------------------------------------------------------------------------*/
type PagePanelHeaderElement = ElementRef<'div'>;
type PagePanelHeaderComponentProps = ComponentPropsWithoutRef<'div'>;
type PagePanelHeaderProps = PagePanelHeaderComponentProps;

const PagePanelHeader = forwardRef<
  PagePanelHeaderElement,
  PropsWithChildren<PagePanelHeaderProps>
>(({ children, className, ...rest }, ref) => {
  return (
    <div {...rest} ref={ref} className={clsx('page-panel-header', className)}>
      {children}
    </div>
  );
});

PagePanelHeader.displayName = 'PagePanelHeader';

/* -------------------------------------------------------------------------------------------------
 * PagePanelTitle
 * -----------------------------------------------------------------------------------------------*/
type PagePanelTitleElement = ElementRef<typeof Typography>;
type PagePanelTitleComponentProps = ComponentPropsWithoutRef<typeof Typography>;
type PagePanelTitleProps = Omit<
  PagePanelTitleComponentProps,
  'variant' | 'paragraph'
>;

const PagePanelTitle = forwardRef<
  PagePanelTitleElement,
  PropsWithChildren<PagePanelTitleProps>
>(({ children, className, ...rest }, ref) => {
  return (
    <Typography
      {...rest}
      variant="h2"
      ref={ref}
      className={clsx('page-panel-title', className)}
    >
      {children}
    </Typography>
  );
});

PagePanelTitle.displayName = 'PagePanelTitle';

/* -------------------------------------------------------------------------------------------------
 * PagePanelDescription
 * -----------------------------------------------------------------------------------------------*/
type PagePanelDescriptionElement = ElementRef<typeof Typography>;
type PagePanelDescriptionComponentProps = ComponentPropsWithoutRef<
  typeof Typography
>;
type PagePanelDescriptionProps = Omit<
  PagePanelDescriptionComponentProps,
  'variant' | 'paragraph'
>;

const PagePanelDescription = forwardRef<
  PagePanelDescriptionElement,
  PropsWithChildren<PagePanelDescriptionProps>
>(({ children, className, ...rest }, ref) => {
  return (
    <Typography
      {...rest}
      ref={ref}
      className={clsx('page-panel-description', className)}
      sx={(theme) => ({ maxWidth: theme.maxWidth.paragraph })}
    >
      {children}
    </Typography>
  );
});

PagePanelDescription.displayName = 'PagePanelDescription';

/* -------------------------------------------------------------------------------------------------
 * PagePreviewPanel
 * -----------------------------------------------------------------------------------------------*/
type PagePreviewPanelElement = ElementRef<typeof Panel>;
type PagePreviewPanelComponentProps = ComponentPropsWithoutRef<typeof Panel>;
type PagePreviewPanelProps = Omit<
  PagePreviewPanelComponentProps,
  'collapsedSize'
> & {
  resizable?: boolean;
};

const PagePreviewPanel = forwardRef<
  PagePreviewPanelElement,
  PropsWithChildren<PagePreviewPanelProps>
>(
  (
    {
      collapsible = false,
      resizable = true,
      children,
      className,
      defaultSize = 25,
      minSize = 25,
      maxSize = 50,
      ...rest
    },
    ref,
  ) => {
    /**
     * subscribe to page context
     */
    const { collapsed, collapse, expand, previewPanelRef, setCollapsible } =
      usePageContext();

    /**
     * merge ref instances
     */
    const mergedRef = composeRefs([ref, previewPanelRef]);

    /**
     * handles the transition end event that removes the transition class
     */
    const handleTransitionEnd = () => {
      const panelElement = getPanelElement(PREVIEW_PANEL_ID);

      if (panelElement) {
        panelElement.classList.remove(PANEL_TRANSITION_CLASS);
      }
    };

    /**
     * sync collapsed state with preview panel ref state
     * when the component mounts
     */
    useEffect(() => {
      if (previewPanelRef.current == null) {
        return;
      }

      if (previewPanelRef.current.isCollapsed()) {
        collapse();
      } else {
        expand();
      }

      // expose collapsible to context
      setCollapsible(collapsible);
    }, [previewPanelRef, collapse, expand, collapsible, setCollapsible]);

    return (
      <>
        <PanelResizeHandle
          className="page-preview-panel-resize"
          disabled={!resizable}
        >
          <span className="page-preview-panel-resize-thumb" />
        </PanelResizeHandle>
        <Panel
          {...rest}
          id="preview"
          ref={mergedRef}
          data-collapsed={collapsed}
          onCollapse={collapse}
          onExpand={expand}
          collapsible={collapsible}
          defaultSize={defaultSize}
          minSize={minSize}
          maxSize={maxSize}
          onTransitionEnd={handleTransitionEnd}
          className={clsx('page-preview-panel', className)}
        >
          <div className="page-panel-overflow">{children}</div>
        </Panel>
      </>
    );
  },
);

PagePreviewPanel.displayName = 'PagePreviewPanel';

/* -------------------------------------------------------------------------------------------------
 * PagePreviewPanelTitle
 * -----------------------------------------------------------------------------------------------*/
type PagePreviewPanelTitleElement = ElementRef<typeof Typography>;
type PagePreviewPanelTitleComponentProps = ComponentPropsWithoutRef<
  typeof Typography
>;
type PagePreviewPanelTitleProps = Omit<
  PagePreviewPanelTitleComponentProps,
  'variant' | 'paragraph'
>;

const PagePreviewPanelTitle = forwardRef<
  PagePreviewPanelTitleElement,
  PropsWithChildren<PagePreviewPanelTitleProps>
>(({ children, className, ...rest }, ref) => {
  return (
    <Typography
      {...rest}
      variant="h2"
      ref={ref}
      className={clsx('page-preview-panel-title', className)}
    >
      {children}
    </Typography>
  );
});

PagePreviewPanelTitle.displayName = 'PagePreviewPanelTitle';

/* -------------------------------------------------------------------------------------------------
 * PageNavTabs
 * -----------------------------------------------------------------------------------------------*/
type PageNavTabsElement = ElementRef<typeof Tabs>;
type PageNavTabsComponentProps = ComponentPropsWithoutRef<typeof Tabs>;
type PageNavTabsProps = Omit<
  PageNavTabsComponentProps,
  'defaultValue' | 'value'
>;

const PageNavTabs = forwardRef<
  PageNavTabsElement,
  PropsWithChildren<PageNavTabsProps>
>(({ children, className, ...rest }, ref) => {
  /**
   * Get the current path name
   */
  const location = useLocation();

  return (
    <Tabs
      {...rest}
      ref={ref}
      value={location.pathname}
      className={clsx('page-nav-tabs', className)}
      variant="scrollable"
      scrollButtons="auto"
    >
      {children}
    </Tabs>
  );
});

PageNavTabs.displayName = 'PageNavTabs';

/* -------------------------------------------------------------------------------------------------
 * PageNavTab
 * -----------------------------------------------------------------------------------------------*/
type PageNavTabElement = ElementRef<typeof Link>;
type PageNavTabLinkProps = ComponentPropsWithoutRef<typeof Link>;
type PageNavTabComponentProps = ComponentPropsWithoutRef<typeof Tab>;

type PageNavTabProps = Omit<PageNavTabComponentProps, 'component' | 'href'> &
  Omit<PageNavTabLinkProps, 'to'>;

const PageNavTab = forwardRef<PageNavTabElement, PageNavTabProps>(
  ({ className, value, ...rest }, ref) => {
    return (
      <Tab
        {...rest}
        ref={ref}
        className={clsx('page-nav-tab', className)}
        component={Link}
        to={value}
        value={value}
      />
    );
  },
);

PageNavTab.displayName = 'PageNavTab';

/* -------------------------------------------------------------------------------------------------
 * PagePanelTabsContent
 * -----------------------------------------------------------------------------------------------*/
type PagePanelTabsContentElement = ElementRef<'div'>;
type PagePanelTabsContentProps = ComponentPropsWithoutRef<'div'>;

const PagePanelTabsContent = forwardRef<
  PagePanelTabsContentElement,
  PropsWithChildren<PagePanelTabsContentProps>
>(({ children, className, ...rest }, ref) => {
  return (
    <div
      {...rest}
      ref={ref}
      className={clsx('page-panel-tabs-content', className)}
    >
      {children}
    </div>
  );
});

PagePanelTabsContent.displayName = 'PagePanelTabsContent';

export {
  Page,
  PageHeader,
  PageTitle,
  PageContent,
  PagePanel,
  PagePanelHeader,
  PageResizableContent,
  PageResizablePanel,
  PagePanelTitle,
  PagePanelDescription,
  PagePreviewPanel,
  PageNavTabs,
  PageNavTab,
  PagePanelTabsContent,
};

export type {
  PageProps,
  PageHeaderProps,
  PageTitleProps,
  PageContentProps,
  PagePanelProps,
  PagePanelHeaderProps,
  PageResizableContentProps,
  PageResizablePanelProps,
  PagePanelTitleProps,
  PagePanelDescriptionProps,
  PagePreviewPanelProps,
  PagePreviewPanelTitleProps,
  PageNavTabsProps,
  PageNavTabProps,
  PagePanelTabsContentProps,
};
